import * as React from 'react';
import { Chip } from 'components/ui/chip';

/**
 * Every design-system release date, newest first.
 *
 * **The standard: dates are pinned, never derived.** Add a literal here when a
 * change set ships, then cite that same literal on the release in `ChangeLog`
 * and on every specimen badge belonging to it.
 *
 * This replaced a single `RELEASE_DATE` constant that `ChangeBadge` used as a
 * default. That was quietly wrong: a badge is a historical claim about when
 * something landed, so "the current release" is exactly the thing it must not
 * mean — every existing badge re-dated itself the moment the constant moved to
 * the next release. Pinning keeps an old badge saying what it always said, and
 * typing the field to this list means a typo or a release that doesn't exist
 * is a compile error rather than a wrong date on a page nobody re-reads.
 */
export const RELEASE_DATES = [
  'Aug 24, 2026',
  'Aug 13, 2026',
  'Aug 8, 2026',
] as const;

export type ReleaseDate = typeof RELEASE_DATES[number];

type Props = {
  /**
   * `new` renders as **In Review** — the specimen landed but has not been
   * signed off yet. The prop name is kept as `new` because it is what every
   * call site passes and it describes the change, not the review state.
   */
  kind: 'new' | 'updated';
  /**
   * Which release this badge claims. Required on purpose — see
   * `RELEASE_DATES`. Pass the literal (`date="Aug 13, 2026"`), not a "current
   * release" alias, or the drift this replaced comes straight back.
   */
  date: ReleaseDate;
};

/**
 * Marks a specimen or tile as recently added or changed.
 *
 * The audit already flags drift in orange (`flagged`); this is the other
 * half — what moved on purpose, and when. Without it a reader can't tell a
 * component that landed yesterday from one that has been stable for a year,
 * which is exactly the question at hand-off.
 */
/**
 * The chrome the audit's own badges wear, shared with `ParityBadge`.
 *
 * These were `Tag`s until Tag was deleted. Chip's base is a step larger and its
 * label slot sets its own type, so these five props restore exactly what Tag
 * rendered — 12px/600 on a 15px line in a 25px pill — rather than introducing
 * anything new. `minH` defeats the 20px floor Chakra's Tag slot recipe puts on
 * the root; without it `py` cannot make the badge shorter.
 *
 * `flexShrink` and `maxW` are not about size — they stop the badge collapsing.
 * Chip's label slot is `overflow: hidden; text-overflow: ellipsis`, and its root
 * carries `max-width: 100%`. `Specimen` hands the badge a zero-width flex slot,
 * so 100% of nothing clipped the label to nothing: "In Review · Aug 24, 2026"
 * rendered as an 18px stub of pure padding. `Tag` was a plain span with no
 * overflow rule, so it simply overflowed the slot and drew at full width. These
 * two restore that, letting the badge size to its own content.
 *
 * Exported because `ParityBadge` sits directly beside these and has to match.
 */
export const AUDIT_BADGE_CHROME = {
  px: 2,
  py: 1,
  minH: 0,
  maxW: 'none',
  flexShrink: 0,
  fontSize: 'xs',
  lineHeight: 'sm',
  letterSpacing: '0.01em',
} as const;

export const ChangeBadge = ({ kind, date }: Props) => (
  <Chip
    variant={kind === 'new' ? 'mint' : 'neutral'}
    {...AUDIT_BADGE_CHROME}
  >
    {kind === 'new' ? 'In Review' : 'Updated'} · {date}
  </Chip>
);
